import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { connect } from 'react-redux';
import { useParams } from 'react-router-dom';
import { CheckCircle2, ChevronLeft, ChevronRight, Circle, Clock, Globe, Leaf, Mail, ShieldCheck, Smartphone, Wind } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Progress } from '../components/ui/progress';
import SurveyStep, { isStepValid } from '../components/Survey/employee/SurveyStep';
import SurveyReviewStep from '../components/Survey/employee/SurveyReviewStep';
import { readWfhDaysPerWeekAnswer, readWorkingDaysPerWeek } from '../components/Survey/employee/WfhDaysPerWeekField';
import { Actions as SURVEY_ACTIONS, extractTokenSurveyResponseData } from '../store/actions/surveyActions';

const prettifySlug = (slug) =>
  String(slug || '')
    .split(/[-_]/)
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
    .join(' ') || 'Step';

/** Home-energy steps omitted when `wfh_days_per_week` is 0 (all office, no WFH footprint). */
const STEPS_HIDDEN_WHEN_NO_WFH = ['wfh-equipment', 'lighting', 'heating', 'cooling', 'renewable'];

const getHiddenSlugsForAnswers = (answers) => {
  const hidden = new Set();
  if (readWfhDaysPerWeekAnswer(answers) === 0) {
    STEPS_HIDDEN_WHEN_NO_WFH.forEach((slug) => hidden.add(slug));
  }
  const wfh = readWfhDaysPerWeekAnswer(answers);
  if (wfh != null) {
    const days = readWorkingDaysPerWeek(answers);
    if (days === wfh) {
      hidden.add('commuting');
    }
  }
  return hidden;
};

const filterStepsByHiddenSlugs = (fullSteps, hiddenSet) =>
  fullSteps.filter((step) => !hiddenSet.has(step.slug));

const mapFullStepIndexToFiltered = (fullSteps, filteredSteps, fullIndex, hiddenSet) => {
  const clamped = Math.min(Math.max(0, fullIndex), Math.max(0, fullSteps.length - 1));
  const slugAt = fullSteps[clamped]?.slug;
  if (slugAt && !hiddenSet.has(slugAt)) {
    const j = filteredSteps.findIndex((s) => s.slug === slugAt);
    if (j >= 0) return j;
  }
  for (let k = clamped + 1; k < fullSteps.length; k++) {
    const s = fullSteps[k]?.slug;
    if (s && !hiddenSet.has(s)) {
      const j = filteredSteps.findIndex((step) => step.slug === s);
      if (j >= 0) return j;
    }
  }
  for (let k = clamped - 1; k >= 0; k--) {
    const s = fullSteps[k]?.slug;
    if (s && !hiddenSet.has(s)) {
      const j = filteredSteps.findIndex((step) => step.slug === s);
      if (j >= 0) return j;
    }
  }
  return 0;
};

const mapFilteredStepIndexToFull = (fullSteps, filteredSteps, filteredIndex) => {
  const clamped = Math.min(Math.max(0, filteredIndex), Math.max(0, filteredSteps.length - 1));
  const slug = filteredSteps[clamped]?.slug;
  const j = fullSteps.findIndex((s) => s.slug === slug);
  return j >= 0 ? j : 0;
};

const stepOrderFromQuestions = (questions = []) => {
  const seen = new Map();
  questions.forEach((question, index) => {
    const slug = question.stepSlug || `step-${index + 1}`;
    const order = Number.isFinite(question.stepOrder) ? question.stepOrder : index;
    const navTitle =
      question.stepNavLabel || question.stepTitle || question.groupName || prettifySlug(slug);
    const sidebarSubtitle = question.stepSubtitle || question.groupDescription || '';
    const badgeLabel = question.stepBadge || prettifySlug(slug);
    const entry = { slug, order, badgeLabel, sidebarTitle: navTitle, sidebarSubtitle };
    if (!seen.has(slug)) {
      seen.set(slug, entry);
      return;
    }
    const existing = seen.get(slug);
    if (order < existing.order) seen.set(slug, entry);
  });
  return Array.from(seen.values())
    .sort((a, b) => a.order - b.order)
    .map(({ slug, badgeLabel, sidebarTitle, sidebarSubtitle }) => ({
      slug,
      badgeLabel,
      sidebarTitle,
      sidebarSubtitle,
    }));
};

const localDraftKey = (token, surveyVersion) => `survey_draft_${token}_${surveyVersion || 'v1'}`;
const startedKey = (token) => `survey_started_${token}`;

const MS_PER_24H = 24 * 60 * 60 * 1000;

const isDeadlineDatePastUtc = (deadlineDate) => {
  if (deadlineDate == null || deadlineDate === '') return false;
  const d = new Date(String(deadlineDate).trim());
  if (Number.isNaN(d.getTime())) return false;
  const deadlineUtc = Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate());
  const now = new Date();
  const todayUtc = Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate());
  return todayUtc > deadlineUtc;
};

/** Campaign closed to new participation: wrong status or deadline passed (uses meta only). */
const campaignSurveyUnavailable = (tokenMeta) => {
  const c = tokenMeta?.campaign;
  if (!c || typeof c !== 'object') return true;
  const status = String(c.status || '').toLowerCase();
  if (status !== 'active' && status !== 'reopened') return true;
  if (isDeadlineDatePastUtc(c.deadlineDate)) return true;
  return false;
};

const isInvitationSurveyFinished = (status) => {
  const s = String(status || '').toLowerCase();
  return s === 'completed' || s === 'submitted';
};

/** Latest `submittedAt` among non-draft responses (ms since epoch), or null. */
const latestFinalSubmittedAtMs = (responsesPayload) => {
  const rows = Array.isArray(responsesPayload?.responses) ? responsesPayload.responses : [];
  let best = null;
  for (const r of rows) {
    if (r?.isDraft === true || String(r?.status || '').toLowerCase() === 'draft') continue;
    const raw = r?.submittedAt;
    if (raw == null || raw === '') continue;
    const t = new Date(raw).getTime();
    if (Number.isNaN(t)) continue;
    if (best == null || t > best) best = t;
  }
  return best;
};

/** Invitation finished and last submission was more than 24 hours ago (requires responses payload). */
const isSurveyLockedAfterSubmission = (tokenMeta, responsesPayload) => {
  if (!isInvitationSurveyFinished(tokenMeta?.invitation?.status)) return false;
  const submittedMs = latestFinalSubmittedAtMs(responsesPayload);
  if (submittedMs == null) return false;
  return Date.now() - submittedMs > MS_PER_24H;
};

const reportingSubtitleDesktop = (tokenMeta) => {
  const year =
    tokenMeta?.campaign?.reportingYear ||
    (tokenMeta?.campaign?.reportingPeriodStart && String(tokenMeta.campaign.reportingPeriodStart).slice(0, 4)) ||
    null;
  const base = year ? `WFH & Commuting Survey · FY ${year}` : 'WFH & Commuting Survey';
  return base.toUpperCase();
};

const DEFAULT_STEP_INTRO =
  'Helps us attribute your responses to the right office and reporting window.';

/** Step shell title + intro by `question.section` / `stepSlug` from the survey API. */
const STEP_SHELL_BY_SECTION = {
  employment: {
    title: 'A bit about you',
    intro: 'Helps us attribute your responses to the right office and reporting window.',
  },
  'working-pattern': {
    title: 'Your working pattern',
    intro: 'Tell us about a typical week in the reporting period.',
  },
  'wfh-equipment': {
    title: 'Your home office',
    intro: 'What kit do you use while working from home? Count yours only, not housemates.',
  },
  lighting: {
    title: 'Lighting',
    intro: 'Do you use extra lighting while working from home?',
  },
  heating: {
    title: 'Heating your home',
    intro: 'Only the hours and area used while you are working from home.',
  },
  cooling: {
    title: 'Cooling & air conditioning',
    intro: 'Only while you are working from home.',
  },
  renewable: {
    title: 'Where does your electricity come from?',
    intro: 'Lets us apply the right emission factor to your home electricity use.',
  },
  commuting: {
    title: 'Your commute',
    intro: 'Your typical journey to the office. Add each leg — e.g. walk, then train, then walk.',
  },
  review: {
    title: 'Review',
    intro: 'Review your answers. Tap Edit to change anything before submitting.',
  },
};

const stepShellCopyForSection = (sectionRaw) => {
  if (sectionRaw == null || sectionRaw === '') return null;
  const key = String(sectionRaw).trim();
  return STEP_SHELL_BY_SECTION[key] || STEP_SHELL_BY_SECTION[key.replace(/_/g, '-')] || null;
};

const organisationDisplayName = (tokenMeta) =>
  tokenMeta?.organisationName ||
  tokenMeta?.organisation?.name ||
  tokenMeta?.campaign?.organisationName ||
  tokenMeta?.campaign?.clientName ||
  'Your organisation';

const monthYearUtcFormatter = new Intl.DateTimeFormat('en-GB', {
  month: 'long',
  year: 'numeric',
  timeZone: 'UTC',
});

const formatMonthYearUtc = (date) => {
  if (!date || Number.isNaN(date.getTime())) return '';
  return monthYearUtcFormatter.format(date);
};

const parseCampaignBoundaryDate = (value) => {
  if (value == null || value === '') return null;
  const d = new Date(String(value).trim());
  return Number.isNaN(d.getTime()) ? null : d;
};

/** Human-readable range for copy (e.g. "January 2025 to December 2025"); falls back to `reportingPeriod` string. */
const reportingPeriodLabelFromCampaign = (campaign) => {
  if (!campaign || typeof campaign !== 'object') return '';
  const start = parseCampaignBoundaryDate(campaign.reportingPeriodStart);
  const end = parseCampaignBoundaryDate(campaign.reportingPeriodEnd);
  if (start && end) {
    return `${formatMonthYearUtc(start)} to ${formatMonthYearUtc(end)}`;
  }
  if (start) return formatMonthYearUtc(start);
  if (end) return formatMonthYearUtc(end);
  if (campaign.reportingPeriod) return String(campaign.reportingPeriod).trim();
  return '';
};

const WORKED_FULL_REPORTING_PERIOD_KEY = 'worked_full_reporting_period';

/** Injects campaign reporting period into the “yes” option for the worked-full-period question (API-driven schema). */
const enrichTokenQuestionsWithReportingPeriod = (questions, campaign) => {
  const periodLabel = reportingPeriodLabelFromCampaign(campaign);
  if (!periodLabel || !Array.isArray(questions)) return questions;

  return questions.map((question) => {
    if (question.key !== WORKED_FULL_REPORTING_PERIOD_KEY || !Array.isArray(question.options)) return question;
    return {
      ...question,
      options: question.options.map((option) => {
        if (option.value !== 'yes') return option;
        const prev = option.description != null ? String(option.description).trim() : '';
        const description = prev ? `${prev} ${periodLabel}` : periodLabel;
        return { ...option, description };
      }),
    };
  });
};

const PublicSurveyApp = ({
  loading,
  error,
  tokenMeta,
  tokenQuestions,
  draftResponseData,
  getTokenSurveyMeta,
  getTokenSurveyQuestions,
  saveTokenResponseDraft,
  submitTokenResponse,
  setLocalDraftResponseData,
  getTokenSurveyResponsesPayload,
}) => {
  const { token } = useParams();
  const [stepIndex, setStepIndex] = useState(0);
  const [answers, setAnswers] = useState({});
  const [showValidation, setShowValidation] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [lastSavedAt, setLastSavedAt] = useState(null);
  const [showWelcome, setShowWelcome] = useState(() => (token ? sessionStorage.getItem(startedKey(token)) !== '1' : true));
  /** `undefined` = responses GET not finished yet; object or `null` = loaded (for gating + draft merge). */
  const [responsesPayload, setResponsesPayload] = useState(undefined);

  const surveyVersion = tokenMeta?.campaign?.surveyVersion || tokenMeta?.campaign?.schemaVersion || 'v1';
  const draftKey = localDraftKey(token, surveyVersion);

  useEffect(() => {
    getTokenSurveyMeta(token);
    getTokenSurveyQuestions(token);
  }, [token, getTokenSurveyMeta, getTokenSurveyQuestions]);

  useEffect(() => {
    setResponsesPayload(undefined);
  }, [token]);

  useEffect(() => {
    if (!token) return undefined;
    let cancelled = false;
    (async () => {
      const payload = await getTokenSurveyResponsesPayload(token);
      if (cancelled) return;
      setResponsesPayload(payload ?? null);

      let parsedLocal = {};
      try {
        const localDraft = localStorage.getItem(draftKey);
        const raw = localDraft ? JSON.parse(localDraft) : {};
        parsedLocal = raw && typeof raw === 'object' && !Array.isArray(raw) ? raw : {};
      } catch {
        parsedLocal = {};
      }
      const localHasAnswers = Object.keys(parsedLocal).length > 0;
      const fromResponses = localHasAnswers ? {} : extractTokenSurveyResponseData(payload) || {};

      const apiDraft = draftResponseData && typeof draftResponseData === 'object' ? draftResponseData : {};
      const metaDraft = tokenMeta?.responseData && typeof tokenMeta.responseData === 'object' ? tokenMeta.responseData : {};
      const mergedDraft = { ...parsedLocal, ...apiDraft, ...metaDraft, ...fromResponses };
      setAnswers(mergedDraft);
    })();
    return () => {
      cancelled = true;
    };
  }, [draftKey, draftResponseData, tokenMeta, token, getTokenSurveyResponsesPayload]);

  const tokenQuestionsEnriched = useMemo(
    () => enrichTokenQuestionsWithReportingPeriod(tokenQuestions, tokenMeta?.campaign),
    [tokenQuestions, tokenMeta?.campaign],
  );

  const stepsFull = useMemo(() => {
    const base = stepOrderFromQuestions(tokenQuestionsEnriched);
    if (base.some((s) => s.slug === 'review')) return base;
    const reviewStep = {
      slug: 'review',
      badgeLabel: 'Review',
      sidebarTitle: 'Review',
      sidebarSubtitle: 'Review before you submit',
    };
    const commutingIdx = base.findIndex((s) => s.slug === 'commuting');
    if (commutingIdx >= 0) {
      return [...base.slice(0, commutingIdx + 1), reviewStep, ...base.slice(commutingIdx + 1)];
    }
    return [...base, reviewStep];
  }, [tokenQuestionsEnriched]);
  const hiddenStepSlugs = useMemo(() => getHiddenSlugsForAnswers(answers), [answers]);
  const stepFilterSignature = useMemo(() => [...hiddenStepSlugs].sort().join('|'), [hiddenStepSlugs]);
  const steps = useMemo(
    () => filterStepsByHiddenSlugs(stepsFull, hiddenStepSlugs),
    [stepsFull, hiddenStepSlugs],
  );

  const prevStepFilterSignatureRef = useRef(undefined);
  const prevFilteredStepsRef = useRef(steps);
  useEffect(() => {
    const prevSig = prevStepFilterSignatureRef.current;
    const oldFiltered = prevFilteredStepsRef.current;

    if (prevSig === undefined) {
      prevStepFilterSignatureRef.current = stepFilterSignature;
      prevFilteredStepsRef.current = steps;
      return;
    }
    if (prevSig === stepFilterSignature) {
      prevFilteredStepsRef.current = steps;
      return;
    }

    const slug =
      oldFiltered && oldFiltered.length > 0
        ? oldFiltered[Math.min(stepIndex, oldFiltered.length - 1)]?.slug
        : undefined;
    let newIdx = slug ? steps.findIndex((s) => s.slug === slug) : -1;
    if (newIdx < 0) {
      const fullIdx = oldFiltered?.length ? mapFilteredStepIndexToFull(stepsFull, oldFiltered, stepIndex) : stepIndex;
      newIdx = mapFullStepIndexToFiltered(stepsFull, steps, fullIdx, hiddenStepSlugs);
    }
    setStepIndex(Math.min(Math.max(0, newIdx), Math.max(0, steps.length - 1)));

    prevStepFilterSignatureRef.current = stepFilterSignature;
    prevFilteredStepsRef.current = steps;
  }, [stepFilterSignature, steps, stepsFull, hiddenStepSlugs, stepIndex]);

  const currentStep = steps[stepIndex];
  const currentStepQuestions = useMemo(
    () =>
      tokenQuestionsEnriched
        .filter((question) => question.stepSlug === currentStep?.slug)
        .sort((a, b) => {
          const aOrder = Number.isFinite(a.questionOrder) ? a.questionOrder : 0;
          const bOrder = Number.isFinite(b.questionOrder) ? b.questionOrder : 0;
          return aOrder - bOrder;
        }),
    [tokenQuestionsEnriched, currentStep],
  );

  const inSurveyFlow = !showWelcome && !submitted;
  const progressPercent = steps.length && inSurveyFlow ? Math.round(((stepIndex + 1) / steps.length) * 100) : 0;
  const clientName = organisationDisplayName(tokenMeta);
  const clientInitial = String(clientName || '?').trim().charAt(0).toUpperCase() || '?';

  useEffect(() => {
    if (!token || !dirty || submitted || showWelcome) return;
    const interval = setInterval(async () => {
      const response = await saveTokenResponseDraft(token, answers);
      if (response) {
        localStorage.setItem(draftKey, JSON.stringify(answers));
        setLastSavedAt(new Date().toISOString());
        setDirty(false);
      }
    }, 30000);
    return () => clearInterval(interval);
  }, [token, answers, dirty, submitted, showWelcome, saveTokenResponseDraft, draftKey]);

  const onAnswerChange = (key, value) => {
    const next = { ...answers, [key]: value };
    setAnswers(next);
    setLocalDraftResponseData(next);
    setDirty(true);
  };

  const saveNow = async () => {
    const response = await saveTokenResponseDraft(token, answers);
    if (response) {
      localStorage.setItem(draftKey, JSON.stringify(answers));
      setLastSavedAt(new Date().toISOString());
      setDirty(false);
    }
  };

  const beginSurvey = () => {
    if (token) sessionStorage.setItem(startedKey(token), '1');
    setShowWelcome(false);
    setStepIndex(0);
  };

  const goToStepBySlug = useCallback((slug) => {
    const idx = steps.findIndex((step) => step.slug === slug);
    if (idx >= 0) {
      setShowValidation(false);
      setStepIndex(idx);
    }
  }, [steps]);

  const sectionTitleForSlug = useCallback(
    (slug) =>
      STEP_SHELL_BY_SECTION[slug]?.title ||
      steps.find((step) => step.slug === slug)?.sidebarTitle ||
      prettifySlug(slug),
    [steps],
  );

  const nextStep = () => {
    if (currentStep?.slug !== 'review' && !isStepValid(currentStepQuestions, answers, currentStep?.slug)) {
      setShowValidation(true);
      return;
    }
    setShowValidation(false);
    setStepIndex((index) => Math.min(index + 1, steps.length - 1));
  };

  const previousStep = () => {
    setShowValidation(false);
    if (stepIndex === 0) {
      if (token) sessionStorage.removeItem(startedKey(token));
      setShowWelcome(true);
      return;
    }
    setStepIndex((index) => Math.max(index - 1, 0));
  };

  const submitSurvey = async () => {
    const response = await submitTokenResponse(token, answers);
    if (response) {
      localStorage.removeItem(draftKey);
      if (token) sessionStorage.removeItem(startedKey(token));
      setSubmitted(true);
      setDirty(false);
    }
  };

  const pageBgClass = 'min-h-screen flex flex-col bg-card lg:bg-background';

  if (loading?.tokenMeta || loading?.tokenQuestions) {
    return (
      <div className={pageBgClass}>
        <div className='flex-1 flex items-center justify-center px-6 py-16'>
          <p className='text-sm text-muted-foreground'>Loading survey…</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={pageBgClass}>
        <div className='flex-1 flex items-center justify-center px-6 py-16'>
          <div className='max-w-md w-full rounded-xl border border-border bg-card p-6 text-center text-sm text-destructive shadow-sm'>
            Unable to load survey: {error}
          </div>
        </div>
      </div>
    );
  }

  if (!tokenMeta || !steps.length) {
    return (
      <div className={pageBgClass}>
        <div className='flex-1 flex items-center justify-center px-6 py-16'>
          <div className='max-w-md w-full rounded-xl border border-border bg-card p-6 text-center text-sm text-muted-foreground shadow-sm'>
            Survey link is invalid or expired.
          </div>
        </div>
      </div>
    );
  }

  if (responsesPayload === undefined) {
    return (
      <div className={pageBgClass}>
        <div className='flex-1 flex items-center justify-center px-6 py-16'>
          <p className='text-sm text-muted-foreground'>Loading survey…</p>
        </div>
      </div>
    );
  }

  if (isSurveyLockedAfterSubmission(tokenMeta, responsesPayload)) {
    return (
      <div className={pageBgClass}>
        <div className='flex-1 flex items-center justify-center px-6 py-16'>
          <div className='max-w-md w-full rounded-xl border border-border bg-card p-6 text-center text-sm text-muted-foreground shadow-sm leading-relaxed'>
            This survey has already been submitted. The link to change your answers is only valid for 24 hours after
            submission.
          </div>
        </div>
      </div>
    );
  }

  if (campaignSurveyUnavailable(tokenMeta)) {
    return (
      <div className={pageBgClass}>
        <div className='flex-1 flex items-center justify-center px-6 py-16'>
          <div className='max-w-md w-full rounded-xl border border-border bg-card p-6 text-center text-sm text-muted-foreground shadow-sm leading-relaxed'>
            This survey is no longer available. The campaign may have closed or the deadline has passed.
          </div>
        </div>
      </div>
    );
  }

  const renderSidebar = () => (
    <aside className='hidden lg:block w-[240px] shrink-0'>
      <div className='sticky top-8'>
        <div className='text-[10px] font-mono uppercase tracking-[0.18em] text-muted-foreground mb-3'>Progress</div>
        {inSurveyFlow ? (
          <div className='flex items-center gap-2 mb-5'>
            <div className='flex-1 h-1.5 rounded-full bg-muted overflow-hidden'>
              <div className='h-full bg-primary transition-all' style={{ width: `${progressPercent}%` }} />
            </div>
            <span className='text-[11px] font-mono text-muted-foreground'>{progressPercent}%</span>
          </div>
        ) : null}
        <ol className='space-y-1'>
          {steps.map((step, i) => {
            const isActive = inSurveyFlow && i === stepIndex;
            const isComplete = inSurveyFlow && i < stepIndex;
            const Icon = isComplete ? CheckCircle2 : Circle;
            return (
              <li key={step.slug}>
                <div
                  className={`w-full text-left flex items-start gap-2.5 px-2.5 py-2 rounded-md ${
                    isActive ? 'bg-primary/10 text-foreground' : 'text-muted-foreground/80'
                  }`}>
                  <Icon
                    size={14}
                    strokeWidth={1.75}
                    className={`mt-0.5 shrink-0 ${
                      isComplete ? 'text-success' : isActive ? 'text-primary' : 'text-muted-foreground/50'
                    }`}
                  />
                  <div className='min-w-0 flex-1'>
                    <div className={`text-[12px] font-medium leading-tight ${isActive ? 'text-foreground' : ''}`}>
                      {step.sidebarTitle}
                    </div>
                    {step.sidebarSubtitle ? (
                      <div className='text-[10px] text-muted-foreground/80 mt-0.5'>{step.sidebarSubtitle}</div>
                    ) : null}
                  </div>
                </div>
              </li>
            );
          })}
        </ol>
      </div>
    </aside>
  );

  const renderWelcome = () => (
    <>
      <div className='flex flex-col py-6'>
        <div className='w-16 h-16 rounded-2xl bg-primary/10 flex items-center justify-center text-accent mb-5 mx-auto'>
          <Leaf size={26} strokeWidth={1.5} />
        </div>
        <h1 className='font-display text-2xl text-center leading-tight mb-3'>Help us measure our footprint</h1>
        <p className='text-[15px] text-muted-foreground text-center leading-relaxed mb-6'>
          A short survey about your working pattern, home office, and commute. Takes about 5 minutes on mobile or desktop.
        </p>

        <div className='space-y-3 mb-8'>
          <div className='flex items-start gap-3'>
            <div className='w-9 h-9 rounded-lg bg-primary/10 flex items-center justify-center text-accent flex-shrink-0'>
              <Clock size={16} strokeWidth={1.5} />
            </div>
            <div>
              <div className='text-sm font-medium'>Under 10 minutes</div>
              <div className='text-xs text-muted-foreground'>
                You can save progress and come back later if you need to grab info (like your car fuel type).
              </div>
            </div>
          </div>
          <div className='flex items-start gap-3'>
            <div className='w-9 h-9 rounded-lg bg-primary/10 flex items-center justify-center text-accent flex-shrink-0'>
              <Smartphone size={16} strokeWidth={1.5} />
            </div>
            <div>
              <div className='text-sm font-medium'>Mobile friendly</div>
              <div className='text-xs text-muted-foreground'>Works well on phones — no login or account needed.</div>
            </div>
          </div>
          <div className='flex items-start gap-3'>
            <div className='w-9 h-9 rounded-lg bg-primary/10 flex items-center justify-center text-accent flex-shrink-0'>
              <ShieldCheck size={16} strokeWidth={1.5} />
            </div>
            <div>
              <div className='text-sm font-medium'>Only shared with your employer</div>
              <div className='text-xs text-muted-foreground'>
                Your answers are only used to calculate aggregate company emissions for sustainability reporting.
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className='space-y-2'>
        <Button size='xl' className='w-full' onClick={beginSurvey}>
          Start the survey
        </Button>
        <p className='text-center text-[11px] text-muted-foreground'>This link is personal to you — please don&apos;t forward it.</p>
      </div>
    </>
  );

  const renderSurveyStep = () => {
    const slug = currentStep?.slug;
    const sectionKey =
      slug === 'review'
        ? 'review'
        : currentStepQuestions[0]?.section || currentStepQuestions[0]?.stepSlug || slug || '';
    const shell = stepShellCopyForSection(sectionKey);
    const stepTitle = shell?.title || currentStep?.sidebarTitle || prettifySlug(currentStep?.slug);
    const stepIntro =
      shell?.intro ||
      tokenMeta?.campaign?.introSubtitle ||
      tokenMeta?.campaign?.employeeIntro ||
      tokenMeta?.campaign?.description ||
      DEFAULT_STEP_INTRO;

    return (
      <div className='flex flex-col flex-1 min-h-0 lg:min-h-[480px] lg:rounded-xl lg:border lg:border-border lg:bg-card lg:shadow-sm lg:p-6 xl:p-8'>
        <div className='mb-5'>
          <h1 className='font-display text-xl leading-tight mb-1.5'>{stepTitle}</h1>
          {stepIntro ? <p className='text-[13px] text-muted-foreground leading-relaxed'>{stepIntro}</p> : null}
        </div>

        <div className='space-y-3'>
          {tokenMeta?.invitation?.status === 'submitted' ? (
            <div className='text-xs text-amber-800 bg-amber-50 border border-amber-200/80 rounded-lg px-3 py-2'>
              This invitation already has a submitted response. Saving or submitting again may overwrite previous answers.
            </div>
          ) : null}
          {tokenMeta?.capabilities?.vehicleLookupEnabled === false ? (
            <div className='text-xs text-muted-foreground'>Vehicle lookup is disabled for this campaign.</div>
          ) : null}
        </div>

        <div className='flex-1 flex flex-col min-h-0'>
          {slug === 'review' ? (
            <SurveyReviewStep
              steps={steps}
              questions={tokenQuestionsEnriched}
              answers={answers}
              onEditSlug={goToStepBySlug}
              sectionTitleForSlug={sectionTitleForSlug}
            />
          ) : (
            <>
              <SurveyStep
                questions={currentStepQuestions}
                answers={answers}
                onAnswerChange={onAnswerChange}
                showValidation={showValidation}
                stepSlug={slug}
              />

              {slug === 'cooling' ? (
                <div className='rounded-lg bg-muted/50 p-3 flex items-start gap-2 text-xs text-muted-foreground mt-4 shrink-0'>
                  <Wind size={12} strokeWidth={1.5} className='mt-0.5 shrink-0' />
                  <span className='leading-relaxed'>
                    Room scope uses a different multiplier for cooling (1/2/4 cooling units) vs heating (0.25/0.5/1.0 area
                    share). We apply the right one automatically.
                  </span>
                </div>
              ) : null}
            </>
          )}

          <div className='lg:hidden pt-4 mt-4 border-t border-border space-y-3 shrink-0'>
            {stepIndex < steps.length - 1 ? (
              <Button type='button' size='lg' className='w-full' onClick={nextStep}>
                Continue
                <ChevronRight size={14} strokeWidth={1.5} />
              </Button>
            ) : (
              <Button type='button' size='lg' className='w-full' onClick={submitSurvey} loading={Boolean(loading?.submit)}>
                Submit response
              </Button>
            )}
            <div className='flex items-center justify-between'>
              <Button type='button' variant='ghost' size='sm' className='-ml-3' onClick={previousStep}>
                <ChevronLeft size={12} strokeWidth={1.5} />
                Back
              </Button>
              <Button type='button' variant='text' size='sm' className='-mr-1' onClick={saveNow} loading={Boolean(loading?.saveDraft)}>
                {'Save & continue later'}
              </Button>
            </div>
          </div>

          <div className='hidden lg:flex flex-row items-center justify-between gap-4 pt-6 mt-4 border-t border-border shrink-0'>
            <div className='text-xs text-muted-foreground'>
              {lastSavedAt ? `Last saved: ${new Date(lastSavedAt).toLocaleTimeString()}` : 'Not saved yet'}
            </div>
            <div className='flex items-center gap-3'>
              <Button type='button' variant='ghost' size='default' className='-ml-3 text-muted-foreground' onClick={previousStep}>
                Back
              </Button>
              <Button type='button' variant='text' size='sm' onClick={saveNow} loading={Boolean(loading?.saveDraft)}>
                Save draft
              </Button>
              {stepIndex < steps.length - 1 ? (
                <Button type='button' size='default' onClick={nextStep}>
                  Continue
                  <ChevronRight size={14} strokeWidth={1.5} />
                </Button>
              ) : (
                <Button type='button' size='default' onClick={submitSurvey} loading={Boolean(loading?.submit)}>
                  Submit response
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };

  const layoutShell = (body) => (
    <div className={pageBgClass}>
      <header className='border-b border-border bg-card'>
        <div className='max-w-[1200px] mx-auto px-5 lg:px-8 pt-4 pb-3 flex items-center justify-between'>
          <div className='flex items-center gap-2 min-w-0'>
            <div className='w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center text-accent font-display text-sm font-bold shrink-0'>
              {clientInitial}
            </div>
            <div className='min-w-0'>
              <div className='text-[13px] font-semibold leading-tight truncate'>{clientName}</div>
                <div className='text-[10px] text-muted-foreground uppercase tracking-[0.12em] truncate lg:hidden'>
                  WFH &amp; Commuting
                </div>
              <div className='text-[10px] text-muted-foreground tracking-[0.12em] truncate hidden lg:block'>
                {reportingSubtitleDesktop(tokenMeta)}
              </div>
            </div>
          </div>
          <button
            type='button'
            className='flex items-center gap-1 text-[11px] lg:text-[12px] text-muted-foreground hover:text-foreground transition-colors px-2 py-1 lg:px-2.5 lg:py-1.5 rounded-md hover:bg-muted shrink-0'
            title='Language'>
            <Globe size={12} strokeWidth={1.5} className='lg:hidden' />
            <Globe size={13} strokeWidth={1.5} className='hidden lg:block' />
            <span className='lg:hidden'>EN</span>
            <span className='hidden lg:inline'>English</span>
          </button>
        </div>
      </header>

      <div className='flex-1 w-full max-w-[1200px] mx-auto flex flex-col lg:flex-row lg:gap-8 lg:px-8 lg:py-8'>
        {renderSidebar()}
        <div className='flex-1 min-w-0 flex flex-col min-h-0'>
          {inSurveyFlow ? (
            <div className='lg:hidden px-5 pt-3 pb-2'>
              <div className='flex items-center justify-between mb-1.5'>
                <span className='text-[11px] font-mono uppercase tracking-[0.14em] text-muted-foreground'>
                  Step {stepIndex + 1} of {steps.length}
                </span>
                <span className='text-[11px] font-mono text-muted-foreground'>{progressPercent}%</span>
              </div>
              <Progress value={progressPercent} />
            </div>
          ) : null}
          <div
            className={`flex-1 flex flex-col min-h-0 ${
              inSurveyFlow && !showWelcome ? 'px-5 py-5 lg:px-0 lg:py-0' : 'px-5 py-5 lg:px-0 lg:py-0'
            }`}>
            {body}
          </div>
        </div>
      </div>

      <footer className='mt-auto'>
        <div className='lg:hidden px-5 py-2 border-t border-border bg-background/60 flex items-center justify-center gap-1.5 text-[10px] text-muted-foreground'>
          <ShieldCheck size={10} strokeWidth={1.5} className='shrink-0' />
          Your responses are encrypted in transit and tied to your employer only.
        </div>
        <div className='hidden lg:block border-t border-border bg-card'>
          <div className='max-w-[1200px] mx-auto px-5 lg:px-8 py-3 flex flex-col sm:flex-row items-center justify-between gap-3 text-[11px] text-muted-foreground'>
            <div className='flex items-center gap-1.5 text-center sm:text-left'>
              <ShieldCheck size={12} strokeWidth={1.5} className='shrink-0' />
              Your responses are encrypted in transit and tied to your employer only.
            </div>
            <div className='flex flex-wrap items-center justify-center gap-x-3 gap-y-1'>
              <span>Powered by Compare Your Footprint</span>
              <span className='opacity-50 hidden sm:inline'>·</span>
              <button type='button' className='hover:text-foreground transition-colors'>
                Privacy
              </button>
              <button type='button' className='hover:text-foreground transition-colors'>
                Help
              </button>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );

  if (submitted) {
    return layoutShell(
      <div className='flex-1 flex flex-col justify-center py-6 text-center lg:py-0 lg:block lg:text-left lg:rounded-xl lg:border lg:border-border lg:bg-card lg:shadow-sm lg:p-6 xl:p-8'>
        <div className='w-16 h-16 rounded-full bg-success-bg flex items-center justify-center text-success mb-5 mx-auto'>
          <CheckCircle2 size={30} strokeWidth={1.5} />
        </div>
        <h1 className='font-display text-2xl leading-tight mb-3'>
          Thanks — your answers are in
        </h1>
        <p className='text-[15px] text-muted-foreground leading-relaxed mb-6'>
          Your working pattern, home office and commute data has been added to your company's sustainability report.
        </p>
        <div className='rounded-xl border border-border bg-background/50 p-4 mb-5 text-left'>
          <div className='flex items-center gap-2 text-xs uppercase tracking-[0.14em] text-muted-foreground mb-2'>
            <Mail size={12} strokeWidth={1.5} />
            What happens next
          </div>
          <div className='text-sm leading-relaxed'>
            Your employer will see aggregate totals by office location — your individual answers are not shown. If you
            realise you made a mistake, you can use your original email link within 24 hours to amend.
          </div>
        </div>
      </div>,
    );
  }

  return layoutShell(showWelcome ? renderWelcome() : renderSurveyStep());
};

const mapStateToProps = (state) => ({
  loading: state.survey?.loading || {},
  error: state.survey?.error,
  tokenMeta: state.survey?.tokenMeta,
  tokenQuestions: state.survey?.tokenQuestions || [],
  draftResponseData: state.survey?.draftResponseData || {},
});

const mapDispatchToProps = {
  getTokenSurveyMeta: SURVEY_ACTIONS.getTokenSurveyMeta,
  getTokenSurveyQuestions: SURVEY_ACTIONS.getTokenSurveyQuestions,
  getTokenSurveyResponsesPayload: SURVEY_ACTIONS.getTokenSurveyResponsesPayload,
  saveTokenResponseDraft: SURVEY_ACTIONS.saveTokenResponseDraft,
  submitTokenResponse: SURVEY_ACTIONS.submitTokenResponse,
  setLocalDraftResponseData: SURVEY_ACTIONS.setLocalDraftResponseData,
};

export default connect(mapStateToProps, mapDispatchToProps)(PublicSurveyApp);
